/**
 * usePrePlanning — the React binding for the Pre-Planning section.
 *
 * Strict layering: the page/section calls THIS hook; the hook calls
 * {@link PrePlanStore}; the store calls the api facade. The page never imports
 * the store or the api directly.
 *
 * Exposes the month-grouped list (newest month first), a selected pre-plan with
 * its hydrated Markdown content, and create/update/remove mutations. Selection
 * hydrates the full S3 body on demand (the list holds only pointer-row
 * summaries). Tenant scoping is automatic (the store routes by the active team
 * bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { PrePlanStore } from './prePlanStore';
import { groupByMonthDesc, sortPrePlansByRecency } from './prePlanModel';
import type {
  PrePlan,
  PrePlanDraft,
  PrePlanMonthGroup,
  PrePlanSummary,
} from './types';

export interface UsePrePlanningResult {
  /** All non-deleted summaries, newest-first. */
  prePlans: PrePlanSummary[];
  /** Summaries grouped by month, months newest-first. */
  groups: PrePlanMonthGroup[];
  /** The currently selected summary, or null. */
  selectedId: string | null;
  /** The hydrated full record for the selected summary, or null. */
  selected: PrePlan | null;
  /** True while the selected record's S3 body is being hydrated. */
  selecting: boolean;
  currentAlias: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Selects a summary by id (hydrates its content); pass null to clear. */
  select: (id: string | null) => Promise<void>;
  get: (id: string) => Promise<PrePlan | undefined>;
  create: (draft: PrePlanDraft) => Promise<PrePlan>;
  update: (id: string, draft: PrePlanDraft) => Promise<PrePlan | undefined>;
  remove: (id: string) => Promise<void>;
}

export function usePrePlanning(): UsePrePlanningResult {
  const api = useApi();
  const store = useMemo(() => new PrePlanStore(api), [api]);

  const [prePlans, setPrePlans] = useState<PrePlanSummary[]>([]);
  const [currentAlias, setCurrentAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<PrePlan | null>(null);
  const [selecting, setSelecting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, identity] = await Promise.all([
        store.listSummaries(),
        api.identityProvider.getIdentity(),
      ]);
      setPrePlans(sortPrePlansByRecency(rows));
      setCurrentAlias(identity.alias);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load pre-plans.');
    } finally {
      setLoading(false);
    }
  }, [store, api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups = useMemo(() => groupByMonthDesc(prePlans), [prePlans]);

  const select = useCallback(
    async (id: string | null) => {
      if (!id) {
        setSelectedId(null);
        setSelected(null);
        return;
      }
      setSelectedId(id);
      setSelecting(true);
      try {
        const full = await store.get(id);
        // Ignore a stale hydration if the selection changed meanwhile.
        setSelected(full ?? null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load pre-plan.');
        setSelected(null);
      } finally {
        setSelecting(false);
      }
    },
    [store],
  );

  const get = useCallback((id: string) => store.get(id), [store]);

  const create = useCallback(
    async (draft: PrePlanDraft) => {
      const saved = await store.create(draft);
      await refresh();
      await select(saved.id);
      return saved;
    },
    [store, refresh, select],
  );

  const update = useCallback(
    async (id: string, draft: PrePlanDraft) => {
      const existing = await store.get(id);
      if (!existing) return undefined;
      const saved = await store.applyDraft(existing, draft);
      await refresh();
      if (selectedId === id) setSelected(saved);
      return saved;
    },
    [store, refresh, selectedId],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      if (selectedId === id) {
        setSelectedId(null);
        setSelected(null);
      }
      await refresh();
    },
    [store, refresh, selectedId],
  );

  return {
    prePlans,
    groups,
    selectedId,
    selected,
    selecting,
    currentAlias,
    loading,
    error,
    refresh,
    select,
    get,
    create,
    update,
    remove,
  };
}
